import {
  BehaviorSubject,
  distinctUntilChanged,
  Observable,
  skip,
  Subject,
} from 'rxjs';
import {defer, sum} from 'lodash';
import {v4 as uuid} from 'uuid';
import LRUCache from 'lru-cache';
import {
  Client,
  ClientErrorResponse,
  ClientEvent,
  ClientEventType,
  ClientResponse,
  ClientStatus,
  ClientUrls,
  ConversationChannel,
  ConversationEvent,
  ConversationEventType,
  ConversationParticipant,
  EmailIssueClient,
  GetConversationMessagesParams,
  GetConversationMessagesResponse,
  GetConversationParticipantsParams,
  GetConversationParticipantsResponse,
  GetVerificationCodesParams,
  GetVerificationCodesResponse,
  InProgressParams,
  InProgressResponse,
  LeaveConversationParams,
  ConversationActionParams,
  RequestACallClient,
  RequestACallParams,
  SendConversationMessageParams,
  SendConversationTypingParams,
  StartConversationParams,
  StartConversationResponse,
  SubmitEmailIssueParams,
  StreamTextParams,
  StreamTextResponse,
  SendFeedbackParams,
} from './types';
import {
  Analytics,
  Errors,
  ErrorScope,
  initObservability,
  Metrics,
  Observability,
  ObservabilityConfig,
} from './observability';
import {defaultClientFetch} from './clientFetch';
import {DefaultRequestACallClient} from './request-a-call-client';
import {DefaultEmailIssueClient} from './email-issue-client';

const KB = 1024;

export abstract class AbstractClient implements Client {
  protected clientFetch: typeof fetch;

  protected status$: BehaviorSubject<ClientStatus> =
    new BehaviorSubject<ClientStatus>(ClientStatus.Uninitialized);

  protected listeners: Set<(event: ClientEvent) => void> = new Set();

  protected conversationListeners: Record<
    string,
    Set<(event: ConversationEvent) => void>
  > = {};

  protected events$: Subject<ClientEvent> = new Subject();

  protected conversationEvents$: Subject<ConversationEvent> = new Subject();

  protected participantCache: LRUCache<string, ConversationParticipant> =
    new LRUCache({
      max: 500,
      maxSize: 512 * KB,
      sizeCalculation: (p) => sum(Object.values(p).map((p) => p?.length || 1)),
    });

  protected urls?: Partial<ClientUrls>;

  protected observability: Observability;

  protected requestACallClient: RequestACallClient;

  protected emailIssueClient: EmailIssueClient;

  abstract channel: ConversationChannel;

  abstract type: string;

  abstract disconnect(): Promise<void>;

  abstract startConversation(
    params: StartConversationParams,
  ): Promise<StartConversationResponse>;

  abstract leaveConversation(
    params: LeaveConversationParams,
  ): Promise<ClientResponse>;

  abstract getConversationMessages(
    params: GetConversationMessagesParams,
  ): Promise<GetConversationMessagesResponse>;

  abstract getConversationParticipants(
    params: GetConversationParticipantsParams,
  ): Promise<GetConversationParticipantsResponse>;

  abstract sendConversationMessage(
    params: SendConversationMessageParams,
  ): Promise<ClientResponse>;

  abstract sendConversationTyping(
    params: SendConversationTypingParams,
  ): Promise<ClientResponse>;

  abstract sendFeedback(params: SendFeedbackParams): Promise<ClientResponse>;

  async streamText(_params: StreamTextParams): Promise<StreamTextResponse> {
    throw new Error('not implemented');
  }

  abstract action(params: ConversationActionParams): Promise<ClientResponse>;

  abstract inProgress(params: InProgressParams): Promise<InProgressResponse>;

  constructor({
    clientFetch = defaultClientFetch,
    urls,
    observability,
  }: {
    clientFetch?: typeof fetch;
    urls?: Partial<ClientUrls>;
    observability?: ObservabilityConfig;
  } = {}) {
    this.clientFetch = clientFetch;

    this.urls = urls;
    this.observability = initObservability(observability);

    this.requestACallClient = new DefaultRequestACallClient({
      urls,
      observability,
    });
    this.emailIssueClient = new DefaultEmailIssueClient({urls, observability});

    if (window) {
      window.addEventListener('online', () => {
        if (this.getStatus() === ClientStatus.Offline) {
          // this conditional check is to allow the client implementation
          // to take precedence if it's changed the status
          this.status$.next(ClientStatus.Reconnecting);
        }
      });
      window.addEventListener('offline', () => {
        this.status$.next(ClientStatus.Offline);
      });
    }

    this.status$.subscribe((status) => {
      this.emitNewEvent({name: 'client-status', status});
    });

    this.events$.subscribe((event) => {
      this.listeners.forEach((listener) => listener(event));
    });

    this.conversationEvents$.subscribe((event) => {
      const listeners = this.conversationListeners[event.conversationId];
      if (listeners) {
        listeners.forEach((listener) => {
          listener(event);
        });
      }
    });
  }

  get analytics(): Analytics {
    return this.observability.analytics;
  }

  get errors(): Errors {
    return this.observability.errors;
  }

  get metrics(): Metrics {
    return this.observability.metrics;
  }

  getStatus(): ClientStatus {
    return this.status$.getValue();
  }

  requestACall(params: RequestACallParams): Promise<ClientResponse> {
    return this.requestACallClient.requestACall(params);
  }

  getVerificationCodes(
    params: GetVerificationCodesParams,
  ): Promise<GetVerificationCodesResponse> {
    return this.requestACallClient.getVerificationCodes(params);
  }

  submitEmailIssue(params: SubmitEmailIssueParams): Promise<ClientResponse> {
    return this.emailIssueClient.submitEmailIssue(params);
  }

  statusObservable(): Observable<ClientStatus> {
    return this.status$.asObservable().pipe(
      // only emit distinct changes; no back-to-back repeats
      distinctUntilChanged(),
      // skip(1) skips initial value; only emit changes
      skip(1),
    );
  }

  eventsObservable(): Observable<ClientEvent> {
    return this.events$.asObservable();
  }

  conversationEventsObservable(): Observable<ConversationEvent> {
    return this.conversationEvents$.asObservable();
  }

  addListener(listener: (event: ClientEvent) => void): void {
    this.listeners.add(listener);
  }

  removeListener(listener: (event: ClientEvent) => void): void {
    this.listeners.delete(listener);
  }

  addConversationListener(
    id: string,
    listener: (event: ConversationEvent) => void,
  ): void {
    const listeners = this.conversationListeners[id] || new Set();
    listeners.add(listener);
    this.conversationListeners[id] = listeners;
  }

  removeConversationListener(
    id: string,
    listener: (event: ConversationEvent) => void,
  ): void {
    const listeners = this.conversationListeners[id];
    if (listeners) {
      listeners.delete(listener);
    }
  }

  protected get tokenUrl(): string | undefined {
    return this.urls?.tokenUrl;
  }

  protected get conversationUrl(): string | undefined {
    return this.urls?.conversationUrl;
  }

  protected emitEvent(event: ClientEvent): void {
    defer(() => this.events$.next(event));
  }

  protected emitNewEvent<T extends ClientEventType>(event: T): void {
    this.emitEvent(this.newEvent(event));
  }

  protected emitConversationEvent(event: ConversationEvent): void {
    defer(() => this.conversationEvents$.next(event));
  }

  protected emitNewConversationEvent<T extends ConversationEventType>(
    event: T,
  ): void {
    this.emitConversationEvent(this.newConversationEvent(event));
  }

  protected newEvent = <T extends ClientEventType>(event: T): ClientEvent => {
    return {
      id: uuid(),
      timestamp: Date.now(),
      ...event,
    };
  };

  protected newConversationEvent = <T extends ConversationEventType>(
    event: T,
  ): ConversationEvent => {
    return {
      id: uuid(),
      timestamp: Date.now(),
      ...event,
    };
  };

  protected cacheParticipant(
    conversationId: string,
    participant: ConversationParticipant,
  ): void {
    const key = `${conversationId}-${participant.id}`;
    this.participantCache.set(key, participant);
  }

  protected getCachedParticipant(
    conversationId: string,
    participantId: string,
  ): ConversationParticipant | undefined {
    const key = `${conversationId}-${participantId}`;
    return this.participantCache.get(key);
  }

  protected fetch = (url: RequestInfo | URL, init?: RequestInit) => {
    return this.clientFetch(url, init);
  };

  protected error(
    code: number,
    error: string,
    tags?: ErrorScope['tags'],
  ): Promise<ClientErrorResponse> {
    let eventId: string | undefined;
    if (code >= 500) {
      eventId = this.errors.reportError(error, tags ? {tags} : undefined);
    }
    return Promise.resolve({code, error, sentryEventId: eventId});
  }
}
